/**
 * Match Router (US-023.6 + US-023.7)
 *
 * Computes an athlete-to-brand match score 0–100 and returns a breakdown
 * explaining which dimensions contributed. Scoring runs server-side so the
 * algorithm stays private — the client only sees the final number and
 * human-readable explanations.
 *
 * Weights (per US-023.6 acceptance criteria):
 *   values overlap    30
 *   sport alignment   25
 *   interest/category 20
 *   deal type         15
 *   deal size fit     10
 *   total            100
 *
 * A dimension with no athlete data contributes 0 — we don't give credit
 * for missing signal. This means new athletes with empty profiles see low
 * scores across the board, which nudges them toward filling out their
 * profile (the intended funnel).
 *
 * @package @app/api
 */

import { parseJsonArray } from '@app/validators/schemas';
import { z } from 'zod';
import type { Context } from '../middleware/context';
import { protectedProcedure } from '../middleware/auth';
import { requireFeature } from '../middleware/feature-gate';
import { parseBrand } from './brand.router';

// -----------------------------------------------------------------------------
// Scoring
// -----------------------------------------------------------------------------

export const MATCH_WEIGHTS = {
  values: 30,
  sport: 25,
  interest: 20,
  dealType: 15,
  dealSize: 10,
} as const;

export interface MatchDimension {
  /** 0..1 — fraction of this dimension's max weight that was earned. */
  score: number;
  /** Absolute points contributed (score × weight). */
  points: number;
  /** The dimension's weight cap. */
  max: number;
  /** Tokens that overlapped (for user-facing explanation). */
  overlap: string[];
  /** True iff the athlete supplied enough info for this dimension to matter. */
  hasSignal: boolean;
}

export interface MatchBreakdown {
  total: number; // 0..100
  dimensions: {
    values: MatchDimension;
    sport: MatchDimension;
    interest: MatchDimension;
    dealType: MatchDimension;
    dealSize: MatchDimension;
  };
}

/** Case-insensitive intersection of two string arrays, preserving left casing. */
function intersectI(a: string[], b: string[]): string[] {
  const bLower = new Set(b.map((x) => x.toLowerCase()));
  return a.filter((x) => bLower.has(x.toLowerCase()));
}

/** Jaccard-style overlap: |a ∩ b| / |a ∪ b|. Returns 0 if both empty. */
function jaccard(a: string[], b: string[]): number {
  if (a.length === 0 && b.length === 0) return 0;
  const aSet = new Set(a.map((x) => x.toLowerCase()));
  const bSet = new Set(b.map((x) => x.toLowerCase()));
  let intersect = 0;
  for (const x of aSet) if (bSet.has(x)) intersect++;
  const union = aSet.size + bSet.size - intersect;
  return union === 0 ? 0 : intersect / union;
}

// -----------------------------------------------------------------------------
// Per-dimension scorers
// -----------------------------------------------------------------------------

function scoreValues(userValues: string[], brandCoreValues: string[]): MatchDimension {
  const hasSignal = userValues.length > 0 && brandCoreValues.length > 0;
  if (!hasSignal) {
    return { score: 0, points: 0, max: MATCH_WEIGHTS.values, overlap: [], hasSignal };
  }
  const overlap = intersectI(userValues, brandCoreValues);
  const score = jaccard(userValues, brandCoreValues);
  return {
    score,
    points: Math.round(score * MATCH_WEIGHTS.values),
    max: MATCH_WEIGHTS.values,
    overlap,
    hasSignal: true,
  };
}

function scoreSport(userSport: string | null, brandSportsFocus: string[]): MatchDimension {
  const hasSignal = !!userSport && brandSportsFocus.length > 0;
  if (!hasSignal) {
    return { score: 0, points: 0, max: MATCH_WEIGHTS.sport, overlap: [], hasSignal };
  }
  const focusLower = brandSportsFocus.map((s) => s.toLowerCase());
  const userLower = userSport!.toLowerCase();
  const match = focusLower.includes(userLower);
  return {
    score: match ? 1 : 0,
    points: match ? MATCH_WEIGHTS.sport : 0,
    max: MATCH_WEIGHTS.sport,
    overlap: match ? [userSport!] : [],
    hasSignal: true,
  };
}

function scoreInterest(
  userInterests: string[],
  userPreferredIndustries: string[],
  brandIndustry: string | null,
  brandCategories: string[],
): MatchDimension {
  // Compare athlete's interests + preferred industries against brand's
  // industry + categories (both as opaque string tokens).
  const athlete = [...userInterests, ...userPreferredIndustries];
  const brand = [...(brandIndustry ? [brandIndustry] : []), ...brandCategories];
  const hasSignal = athlete.length > 0 && brand.length > 0;
  if (!hasSignal) {
    return { score: 0, points: 0, max: MATCH_WEIGHTS.interest, overlap: [], hasSignal };
  }
  const overlap = intersectI(athlete, brand);
  // Normalize by the athlete's list — full credit if all their preferences
  // are represented in the brand's taxonomy.
  const score = overlap.length === 0 ? 0 : Math.min(1, overlap.length / Math.max(1, athlete.length));
  return {
    score,
    points: Math.round(score * MATCH_WEIGHTS.interest),
    max: MATCH_WEIGHTS.interest,
    overlap,
    hasSignal: true,
  };
}

function scoreDealType(
  userPreferredDealTypes: string[],
  brandDealTypes: string[],
): MatchDimension {
  const hasSignal = userPreferredDealTypes.length > 0 && brandDealTypes.length > 0;
  if (!hasSignal) {
    return { score: 0, points: 0, max: MATCH_WEIGHTS.dealType, overlap: [], hasSignal };
  }
  const overlap = intersectI(userPreferredDealTypes, brandDealTypes);
  const score =
    overlap.length === 0
      ? 0
      : Math.min(1, overlap.length / Math.max(1, userPreferredDealTypes.length));
  return {
    score,
    points: Math.round(score * MATCH_WEIGHTS.dealType),
    max: MATCH_WEIGHTS.dealType,
    overlap,
    hasSignal: true,
  };
}

function scoreDealSize(
  userMinDealSize: number | null,
  brandMin: number | null,
  brandMax: number | null,
): MatchDimension {
  // Athlete with no declared minimum → skip this dimension.
  const hasSignal = userMinDealSize != null && (brandMin != null || brandMax != null);
  if (!hasSignal) {
    return { score: 0, points: 0, max: MATCH_WEIGHTS.dealSize, overlap: [], hasSignal };
  }
  const athleteMin = userMinDealSize!;
  // Brand's deal range must include something ≥ athlete's minimum to be
  // compatible. If brand's MAX is below athlete's MIN, score 0.
  if (brandMax != null && brandMax < athleteMin) {
    return {
      score: 0,
      points: 0,
      max: MATCH_WEIGHTS.dealSize,
      overlap: [`Brand max ${brandMax} below your min ${athleteMin}`],
      hasSignal: true,
    };
  }
  // Otherwise compatible; full credit.
  return {
    score: 1,
    points: MATCH_WEIGHTS.dealSize,
    max: MATCH_WEIGHTS.dealSize,
    overlap: [
      `${brandMin != null ? `$${brandMin.toLocaleString()}` : '—'}–${brandMax != null ? `$${brandMax.toLocaleString()}` : '—'} covers your min`,
    ],
    hasSignal: true,
  };
}

/**
 * Compute a full match breakdown.
 *
 * Exported (not just router-internal) so other modules can reuse it —
 * e.g., the brand directory uses it to sort by match score without
 * round-tripping through the router.
 */
export function computeMatch(
  athlete: {
    personalValues: string[];
    interests: string[];
    preferredIndustries: string[];
    preferredDealTypes: string[];
    sport: string | null;
    minimumDealSize: number | null;
  },
  brand: {
    coreValues: string[];
    sportsFocus: string[];
    industry: string | null;
    categories: string[];
    dealTypes: string[];
    dealRangeMin: number | null;
    dealRangeMax: number | null;
  },
): MatchBreakdown {
  const values = scoreValues(athlete.personalValues, brand.coreValues);
  const sport = scoreSport(athlete.sport, brand.sportsFocus);
  const interest = scoreInterest(
    athlete.interests,
    athlete.preferredIndustries,
    brand.industry,
    brand.categories,
  );
  const dealType = scoreDealType(athlete.preferredDealTypes, brand.dealTypes);
  const dealSize = scoreDealSize(
    athlete.minimumDealSize,
    brand.dealRangeMin,
    brand.dealRangeMax,
  );

  const total =
    values.points + sport.points + interest.points + dealType.points + dealSize.points;

  return {
    total: Math.max(0, Math.min(100, total)),
    dimensions: { values, sport, interest, dealType, dealSize },
  };
}

// -----------------------------------------------------------------------------
// Router
// -----------------------------------------------------------------------------

async function loadAthlete(db: Context['db'], userId: string) {
  const user = await db.user.findUnique({
    where: { id: userId },
    select: {
      sport: true,
      personalValues: true,
      interests: true,
      causes: true,
      preferredDealTypes: true,
      preferredIndustries: true,
      minimumDealSize: true,
      openToDeals: true,
    },
  });
  if (!user) throw new Error('User not found');

  return {
    sport: user.sport ?? null,
    personalValues: parseJsonArray(user.personalValues),
    interests: parseJsonArray(user.interests),
    preferredDealTypes: parseJsonArray(user.preferredDealTypes),
    preferredIndustries: parseJsonArray(user.preferredIndustries),
    minimumDealSize: user.minimumDealSize ? Number(user.minimumDealSize) : null,
    openToDeals: user.openToDeals,
  };
}

/**
 * True iff the athlete has enough profile data for match scores to be
 * meaningful. Routes can use this to decide whether to render badges.
 */
function hasScorableProfile(athlete: Awaited<ReturnType<typeof loadAthlete>>): boolean {
  return (
    athlete.personalValues.length > 0 ||
    !!athlete.sport ||
    athlete.interests.length > 0 ||
    athlete.preferredIndustries.length > 0 ||
    athlete.preferredDealTypes.length > 0 ||
    athlete.minimumDealSize != null
  );
}

export const matchRouter = {
  /**
   * Score a single brand for the current athlete. Returns a breakdown
   * (used by the brand detail page, US-023.7).
   */
  forBrand: protectedProcedure
    .use(requireFeature('brandMatching'))
    .input(z.object({ brandId: z.string() }))
    .handler(async ({ input, context }) => {
      const { db } = context;
      const athlete = await loadAthlete(db, context.session.user.id);
      const rawBrand = await db.brand.findUnique({ where: { id: input.brandId } });
      if (!rawBrand) throw new Error('Brand not found');
      const brand = parseBrand(rawBrand);
      const scorable = hasScorableProfile(athlete);
      return {
        scorable,
        breakdown: computeMatch(athlete, brand),
      };
    }),

  /**
   * Score many brands in one shot. Used by the directory to show badges
   * without N+1 round-trips. Capped at 100 ids per call.
   */
  forBrands: protectedProcedure
    .use(requireFeature('brandMatching'))
    .input(z.object({ brandIds: z.array(z.string()).max(100) }))
    .handler(async ({ input, context }) => {
      const { db } = context;
      const athlete = await loadAthlete(db, context.session.user.id);
      const scorable = hasScorableProfile(athlete);
      const brands = await db.brand.findMany({
        where: { id: { in: input.brandIds } },
      });
      const scores: Record<string, number> = {};
      for (const raw of brands) {
        const b = parseBrand(raw);
        scores[b.id] = computeMatch(athlete, b).total;
      }
      return { scorable, scores };
    }),
};
